import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

type PageHeaderProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  hideTitleInBody?: boolean;
};

export function PageHeader({ title, description, actions, className = '', hideTitleInBody = false }: PageHeaderProps) {
  const { setPageTitle } = useAuth();

  useEffect(() => {
    if (typeof title === 'string') {
      setPageTitle(title);
    } else if (title != null) {
      setPageTitle('');
    }
    return () => {
    };
  }, [title, setPageTitle]);

  return (
    <div className={`${hideTitleInBody && !actions ? '!mb-6 !min-h-0 !py-0' : 'mb-8'} flex w-full flex-col items-start justify-start gap-4 text-left sm:flex-row sm:items-end sm:justify-between ${className}`}>
      {!hideTitleInBody ? (
        <div className="min-w-0 w-full flex-1 pl-0 text-left">
          <h1 className="w-full text-left text-[28px] font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-[32px]">
            {title}
          </h1>
          {description ? (
            <p className="mt-2.5 max-w-4xl w-full text-left text-[15px] leading-relaxed text-slate-500">
              {description}
            </p>
          ) : null}
        </div>
      ) : description ? (
        <div className="relative min-w-0 w-full flex-1 overflow-hidden rounded-r-[14px] border-l-[4px] border-l-blue-500 bg-gradient-to-r from-blue-50/80 via-blue-50/50 to-transparent px-5 py-4">
          <p className="max-w-4xl text-left text-[15px] font-semibold leading-relaxed text-slate-700 sm:text-[16px]">
            {description}
          </p>
        </div>
      ) : null}
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export function SectionCard(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <div
      {...rest}
      className={`rounded-xl border border-slate-200/80 bg-white/90 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_-1px_rgba(15,23,42,0.04)] backdrop-blur ${className}`}
    >
      {children}
    </div>
  );
}

export function CardHeader(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <div
      {...rest}
      className={`flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between ${className}`}
    >
      {children}
    </div>
  );
}

export function CardBody(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <div {...rest} className={`px-5 py-5 ${className}`}>
      {children}
    </div>
  );
}

export function CardFooter(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <div
      {...rest}
      className={`flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between ${className}`}
    >
      {children}
    </div>
  );
}

type StatCardProps = {
  icon: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
  trend?: React.ReactNode;
  variant?: 'primary' | 'deep' | 'sky' | 'navy' | 'teal' | 'rich' | 'amber' | 'emerald' | 'slate' | 'purple';
  className?: string;
};

export function StatCard({ icon, label, value, trend, variant = 'primary', className = '' }: StatCardProps) {
  const variants: Record<NonNullable<StatCardProps['variant']>, { card: string; title: string; value: string; trend: string; iconBox: string }> = {
    primary: {
      card: 'bg-gradient-to-br from-blue-50 via-blue-50 to-white border border-blue-200 shadow-[0_1px_2px_0_rgba(37,99,235,0.06),0_10px_28px_-14px_rgba(37,99,235,0.22)]',
      title: 'text-blue-700',
      value: 'text-blue-900',
      trend: 'text-blue-700',
      iconBox: 'bg-blue-600 text-white shadow-[0_4px_16px_-4px_rgba(37,99,235,0.6)]',
    },
    deep: {
      card: 'bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 border border-blue-700 shadow-[0_4px_24px_-10px_rgba(37,99,235,0.65)]',
      title: 'text-blue-100',
      value: 'text-white',
      trend: 'text-blue-100/95',
      iconBox: 'bg-white/15 text-white ring-1 ring-inset ring-white/30 shadow-[0_4px_16px_-6px_rgba(15,23,42,0.4)]',
    },
    sky: {
      card: 'bg-gradient-to-br from-sky-50 via-sky-50 to-white border border-sky-200 shadow-[0_1px_2px_0_rgba(14,165,233,0.06),0_10px_28px_-14px_rgba(14,165,233,0.22)]',
      title: 'text-sky-700',
      value: 'text-sky-900',
      trend: 'text-sky-700',
      iconBox: 'bg-sky-600 text-white shadow-[0_4px_16px_-4px_rgba(14,165,233,0.6)]',
    },
    navy: {
      card: 'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-900 border border-slate-700 shadow-[0_4px_24px_-10px_rgba(15,23,42,0.7)]',
      title: 'text-slate-200',
      value: 'text-white',
      trend: 'text-slate-200/95',
      iconBox: 'bg-blue-500 text-white ring-1 ring-blue-400/40 shadow-[0_4px_16px_-6px_rgba(15,23,42,0.6)]',
    },
    teal: {
      card: 'bg-gradient-to-br from-cyan-50 via-cyan-50 to-white border border-cyan-200 shadow-[0_1px_2px_0_rgba(6,182,212,0.06),0_10px_28px_-14px_rgba(6,182,212,0.22)]',
      title: 'text-cyan-700',
      value: 'text-cyan-900',
      trend: 'text-cyan-700',
      iconBox: 'bg-cyan-600 text-white shadow-[0_4px_16px_-4px_rgba(6,182,212,0.6)]',
    },
    rich: {
      card: 'bg-gradient-to-br from-indigo-50 via-indigo-50 to-white border border-indigo-200 shadow-[0_1px_2px_0_rgba(99,102,241,0.06),0_10px_28px_-14px_rgba(99,102,241,0.22)]',
      title: 'text-indigo-700',
      value: 'text-indigo-900',
      trend: 'text-indigo-700',
      iconBox: 'bg-indigo-600 text-white shadow-[0_4px_16px_-4px_rgba(99,102,241,0.6)]',
    },
    slate: {
      card: 'bg-gradient-to-br from-slate-50 via-slate-50 to-white border border-slate-200 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_10px_28px_-14px_rgba(15,23,42,0.14)]',
      title: 'text-slate-600',
      value: 'text-slate-900',
      trend: 'text-slate-500',
      iconBox: 'bg-slate-700 text-white shadow-[0_4px_16px_-4px_rgba(15,23,42,0.45)]',
    },
    emerald: {
      card: 'bg-gradient-to-br from-emerald-50 via-emerald-50 to-white border border-emerald-200 shadow-[0_1px_2px_0_rgba(16,185,129,0.06),0_10px_28px_-14px_rgba(16,185,129,0.22)]',
      title: 'text-emerald-700',
      value: 'text-emerald-900',
      trend: 'text-emerald-700',
      iconBox: 'bg-emerald-600 text-white shadow-[0_4px_16px_-4px_rgba(16,185,129,0.55)]',
    },
    amber: {
      card: 'bg-gradient-to-br from-amber-50 via-amber-50 to-white border border-amber-200 shadow-[0_1px_2px_0_rgba(245,158,11,0.06),0_10px_28px_-14px_rgba(245,158,11,0.24)]',
      title: 'text-amber-700',
      value: 'text-amber-900',
      trend: 'text-amber-700',
      iconBox: 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-[0_4px_16px_-4px_rgba(245,158,11,0.55)]',
    },
    purple: {
      card: 'bg-gradient-to-br from-purple-50 via-purple-50 to-white border border-purple-200 shadow-[0_1px_2px_0_rgba(168,85,247,0.06),0_10px_28px_-14px_rgba(168,85,247,0.22)]',
      title: 'text-purple-700',
      value: 'text-purple-900',
      trend: 'text-purple-700',
      iconBox: 'bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white shadow-[0_4px_16px_-4px_rgba(168,85,247,0.55)]',
    },
  };

  const v = variants[variant];

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border backdrop-blur ${v.card} ${className}`}
    >
      <div
        className="pointer-events-none absolute -top-20 -right-16 h-44 w-44 rounded-full opacity-[0.22]"
        style={{
          background: variant === 'navy' || variant === 'deep'
            ? 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 70%)'
            : 'radial-gradient(circle, rgba(37,99,235,1) 0%, rgba(37,99,235,0) 70%)',
        }}
        aria-hidden="true"
      />
      <div className="relative flex h-full w-full flex-col gap-3 px-5 py-4 sm:px-6 sm:py-5">
        <div className="relative flex items-start justify-between gap-3">
          <div className="group/label relative flex min-w-0 flex-1 flex-col items-start">
            <span
              className={`inline-block min-w-0 max-w-full truncate text-[13px] font-bold uppercase tracking-[0.16em] leading-snug sm:text-[14px] ${v.title}`}
              title=""
            >
              {label}
            </span>
            <div
              className={`pointer-events-none absolute left-1/2 z-20 mt-[6px] -translate-x-1/2 -translate-y-1 whitespace-normal break-words rounded-[10px] px-3 py-2 text-center text-[12px] font-semibold normal-case tracking-normal shadow-[0_10px_24px_-10px_rgba(15,23,42,0.55)] opacity-0 transition-all duration-200 ease-out group-hover/label:pointer-events-none group-hover/label:translate-y-0 group-hover/label:opacity-100 top-full ${
                variant === 'navy' || variant === 'deep'
                  ? 'bg-white text-slate-900 ring-1 ring-slate-200/70'
                  : 'bg-slate-900 text-white'
              }`}
              style={{ minWidth: 'min(240px, max-content)', maxWidth: '280px' }}
            >
              {label}
              <div
                className={`absolute left-1/2 -top-1 h-2 w-2 -translate-x-1/2 rotate-45 ${
                  variant === 'navy' || variant === 'deep'
                    ? 'bg-white ring-1 ring-slate-200/70'
                    : 'bg-slate-900'
                }`}
                aria-hidden="true"
              />
            </div>
          </div>
          <div
            className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] ${v.iconBox}`}
          >
            {icon}
          </div>
        </div>
        <div className={`mt-1 text-[28px] font-extrabold leading-none tracking-tight sm:text-[32px] ${v.value}`}>
          {value}
        </div>
        {trend ? (
          <div className={`mt-1 text-[13px] font-semibold leading-snug sm:text-[14px] ${v.trend}`}>
            {trend}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ModernTable(props: React.TableHTMLAttributes<HTMLTableElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <div className="relative w-full overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="w-full overflow-x-auto">
        <table {...rest} className={`min-w-full border-collapse text-sm ${className}`}>
          {children}
        </table>
      </div>
    </div>
  );
}

export function Thead(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <thead {...rest} className={`bg-slate-100 border-b border-slate-200 ${className}`}>
      {children}
    </thead>
  );
}

export function Tbody(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <tbody {...rest} className={`divide-y divide-slate-200 bg-white ${className}`}>
      {children}
    </tbody>
  );
}

export function Th(props: React.ThHTMLAttributes<HTMLTableCellElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <th
      {...rest}
      className={`whitespace-nowrap border-r border-slate-200 px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 last:border-r-0 ${className}`}
    >
      {children}
    </th>
  );
}

export function Td(props: React.TdHTMLAttributes<HTMLTableCellElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <td
      {...rest}
      className={`whitespace-nowrap border-r border-slate-200/80 px-4 py-3.5 align-middle text-sm text-slate-700 last:border-r-0 ${className}`}
    >
      {children}
    </td>
  );
}

export function Tr(props: React.HTMLAttributes<HTMLTableRowElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <tr
      {...rest}
      className={`transition-colors even:bg-slate-50/50 hover:bg-blue-50/50 ${className}`}
    >
      {children}
    </tr>
  );
}

export function Button(props: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline'; size?: 'sm' | 'md' | 'lg' | 'icon'; isLoading?: boolean; loadingLabel?: React.ReactNode }) {
  const { variant = 'secondary', size = 'md', className = '', children, type = 'button', isLoading = false, loadingLabel, disabled, ...rest } = props;
  const base =
    'relative inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white disabled:pointer-events-none disabled:opacity-60 disabled:cursor-not-allowed';
  const variants: Record<string, string> = {
    primary:
      'bg-blue-600 text-white shadow-[0_1px_2px_0_rgba(15,23,42,0.06),0_1px_3px_0_rgba(37,99,235,0.2)] hover:bg-blue-700 hover:shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55) focus:ring-blue-500',
    secondary:
      'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:ring-slate-300 shadow-[0_1px_2px_0_rgba(15,23,42,0.04)]',
    danger:
      'bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.06)]',
    success:
      'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.06)]',
    outline:
      'bg-transparent border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:ring-slate-300',
    ghost:
      'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:ring-slate-300',
  };
  const sizes: Record<string, string> = {
    sm: 'h-8 px-2.5 text-xs',
    md: 'h-9 px-3.5 text-sm',
    lg: 'h-10 px-4 text-sm',
    icon: 'h-9 w-9',
  };
  const spinnerSize = size === 'sm' || size === 'icon' ? 'h-4 w-4' : 'h-[18px] w-[18px]';
  const content = isLoading ? (
    <>
      <div className={`animate-spin rounded-full border-[2px] border-current border-t-transparent ${spinnerSize} shrink-0`} aria-hidden="true" />
      <span>{loadingLabel ?? children}</span>
    </>
  ) : (
    children
  );
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={`${base} ${variants[variant] || variants.secondary} ${sizes[size] || sizes.md} ${className}`}
    >
      {content}
    </button>
  );
}

export function IconButton(props: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'ghost' | 'secondary' | 'outline' | 'danger' }) {
  const { variant = 'ghost', className = '', children, ...rest } = props;
  const base =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1';
  const variants = {
    ghost: 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:ring-slate-300',
    secondary:
      'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 focus:ring-slate-300',
    outline:
      'border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 focus:ring-slate-300',
    danger:
      'text-rose-500 hover:bg-rose-50 hover:text-rose-600 focus:ring-rose-300',
  };
  return (
    <button {...rest} className={`${base} ${variants[variant]} ${className}`}>
      {children}
    </button>
  );
}

type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
  tone?: 'slate' | 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'yellow' | 'emerald' | 'orange' | 'navy' | 'cyan';
};

export function Badge({ tone = 'slate', className = '', children, ...rest }: BadgeProps) {
  const tones: Record<NonNullable<BadgeProps['tone']>, string> = {
    slate: 'bg-slate-100 text-slate-700 ring-slate-200',
    blue: 'bg-blue-50 text-blue-700 ring-blue-200',
    navy: 'bg-slate-800 text-slate-100 ring-slate-700/60',
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-800 ring-amber-200',
    yellow: 'bg-yellow-50 text-yellow-800 ring-yellow-200',
    orange: 'bg-orange-50 text-orange-700 ring-orange-200',
    red: 'bg-rose-50 text-rose-700 ring-rose-200',
    purple: 'bg-purple-50 text-purple-700 ring-purple-200',
    cyan: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
  };
  return (
    <span
      {...rest}
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { className = '', ...rest } = props;
  return (
    <input
      {...rest}
      className={`block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-50 disabled:text-slate-500 ${className}`}
    />
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <select
      {...rest}
      className={`block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-50 disabled:text-slate-500 ${className}`}
    >
      {children}
    </select>
  );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { className = '', ...rest } = props;
  return (
    <textarea
      {...rest}
      className={`block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-50 disabled:text-slate-500 ${className}`}
    />
  );
}

export function Label(props: React.LabelHTMLAttributes<HTMLLabelElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <label
      {...rest}
      className={`mb-1.5 block text-xs font-semibold text-slate-700 ${className}`}
    >
      {children}
    </label>
  );
}

export function Modal(props: Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & { onClose?: () => void; title?: React.ReactNode; footer?: React.ReactNode; maxWidthClass?: string }) {
  const { className = '', children, onClose, title, footer, maxWidthClass = 'max-w-xl', ...rest } = props;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 sm:px-6">
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        {...rest}
        className={`relative z-10 w-full ${maxWidthClass} overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_20px_60px_-15px_rgba(15,23,42,0.35)] ${className}`}
      >
        {title !== undefined ? (
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <h3 className="text-base font-semibold tracking-tight text-slate-900">
              {title}
            </h3>
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
                aria-label="Fechar"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
          {children}
        </div>
        {footer !== undefined ? (
          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/70 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

type ConfirmModalProps = {
  isOpen: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  maxWidthClass?: string;
  icon?: React.ReactNode;
};

export function ConfirmModal({
  isOpen,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  confirmVariant = 'danger',
  onConfirm,
  onCancel,
  loading = false,
  maxWidthClass = 'max-w-md',
  icon,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const iconBox = (() => {
    if (icon) return icon;
    if (confirmVariant === 'danger') {
      return (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600 ring-1 ring-inset ring-rose-200">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
      );
    }
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 ring-1 ring-inset ring-blue-200">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
    );
  })();

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6 sm:px-6">
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]"
        onClick={() => !loading && onCancel()}
        aria-hidden="true"
      />
      <div
        className={`relative z-10 w-full ${maxWidthClass} overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_24px_60px_-18px_rgba(15,23,42,0.45)] animate-fadeIn`}
      >
        <div className="px-6 py-6 sm:px-7 sm:pt-7">
          <div className="flex items-start gap-4 sm:items-center">
            {iconBox}
            <div className="min-w-0 flex-1">
              <h3 className="text-[17px] font-bold tracking-tight text-slate-900 leading-snug">
                {title}
              </h3>
              {description ? (
                <p className="mt-1.5 text-[14px] leading-relaxed text-slate-500">
                  {description}
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/70 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={onConfirm}
            isLoading={loading}
            loadingLabel={confirmLabel}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Toast (substitui window.alert() — não trava thread, estilo SharedLayout)
 * ------------------------------------------------------------------------ */

type ToastType = 'success' | 'error' | 'info' | 'warning';
interface ToastItem {
  id: string;
  type: ToastType;
  title: React.ReactNode;
  description?: React.ReactNode;
  durationMs?: number;
}

const toastContainerRef: { current: null | ((t: ToastItem) => void) } = { current: null };
let toastIdCounter = 0;

export function showToast(
  toast: Omit<ToastItem, 'id'> & { type?: ToastType; title: React.ReactNode }
): void {
  const nextId = `toast-${++toastIdCounter}`;
  const payload: ToastItem = {
    id: nextId,
    type: toast.type ?? 'info',
    title: toast.title,
    description: toast.description,
    durationMs: toast.durationMs ?? 3500,
  };
  if (toastContainerRef.current) {
    toastContainerRef.current(payload);
  } else {
    // Fallback: se ToastHost não está montado ainda, usar console (não crasha)
    // eslint-disable-next-line no-console
    console.info('[toast fallback]', payload.type, payload.title, payload.description ?? '');
  }
}

const toastStyles: Record<ToastType, { ring: string; iconBox: string; icon: React.ReactNode }> = {
  success: {
    ring: 'ring-emerald-200/80 bg-emerald-50/95 border-emerald-200',
    iconBox: 'bg-emerald-500 text-white',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
  },
  error: {
    ring: 'ring-rose-200/80 bg-rose-50/95 border-rose-200',
    iconBox: 'bg-rose-500 text-white',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ),
  },
  info: {
    ring: 'ring-blue-200/80 bg-blue-50/95 border-blue-200',
    iconBox: 'bg-blue-500 text-white',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    ),
  },
  warning: {
    ring: 'ring-amber-200/80 bg-amber-50/95 border-amber-200',
    iconBox: 'bg-amber-500 text-white',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
};

export function ToastHost() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    toastContainerRef.current = (t) => {
      setToasts((prev) => [...prev, t]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
      }, t.durationMs);
    };
    return () => {
      toastContainerRef.current = null;
    };
  }, []);

  const remove = (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id));

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-3 px-4 sm:top-6 sm:right-6 sm:left-auto sm:items-end sm:px-0">
      {toasts.map((toast) => {
        const s = toastStyles[toast.type];
        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex w-full max-w-[440px] items-start gap-3 rounded-xl border shadow-[0_20px_60px_-20px_rgba(15,23,42,0.35)] backdrop-blur px-4 py-3.5 ring-1 ring-inset ${s.ring} animate-slideDown`}
            role="status"
          >
            <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${s.iconBox} shadow-[0_4px_12px_-4px_rgba(0,0,0,0.18)]`}>
              {s.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold leading-snug text-slate-900">{toast.title}</div>
              {toast.description ? (
                <div className="mt-0.5 text-[12.5px] leading-relaxed text-slate-600">{toast.description}</div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => remove(toast.id)}
              className="-mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-900/5 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
              aria-label="Fechar aviso"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default {
  PageHeader, SectionCard, CardHeader, CardBody, CardFooter, StatCard,
  ModernTable, Thead, Tbody, Th, Td, Tr,
  Button, IconButton, Badge, Input, Select, Textarea, Label, Modal, ConfirmModal,
  showToast, ToastHost,
};
